import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

function filenameFromDisposition(header, fallback) {
  const value = String(header || '')
  const star = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]) } catch { return star[1] }
  }
  const plain = value.match(/filename="([^"]+)"/i)
  return plain?.[1] || fallback
}


function resolveReportPhotoFileId(user) {
  let storedUser = {}
  try {
    const raw = window?.localStorage?.getItem?.('auth.user')
    storedUser = raw ? JSON.parse(raw) : {}
  } catch {
    storedUser = {}
  }
  return (
    user?.profileReportImageFileId ||
    user?.profilePortraitCutoutFileId ||
    user?.profilePortraitImageFileId ||
    user?.reportImageFileId ||
    user?.reportPhotoFileId ||
    user?.reportPhoto ||
    storedUser?.profileReportImageFileId ||
    storedUser?.profilePortraitCutoutFileId ||
    storedUser?.profilePortraitImageFileId ||
    storedUser?.reportImageFileId ||
    storedUser?.reportPhotoFileId ||
    storedUser?.reportPhoto ||
    ''
  )
}

function formatLocalDateInput(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default function PolicyDeliveryBookDownloadTool() {
  const { user } = useAuth()
  const { show } = useToast()
  const [templates, setTemplates] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [deliveryDate, setDeliveryDate] = useState(() => formatLocalDateInput())
  const [policyNumber, setPolicyNumber] = useState('')
  const [sectionKeys, setSectionKeys] = useState([])

  const selected = useMemo(
    () => templates.find((template) => String(template.id) === String(selectedId)) || null,
    [templates, selectedId],
  )
  const selectedSections = useMemo(
    () => (Array.isArray(selected?.sections) ? selected.sections : []).filter((section) => section?.key && section?.label),
    [selected],
  )
  const canRender = Boolean(selected)
  const hasReportPhoto = Boolean(resolveReportPhotoFileId(user))

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const payload = unwrap(await api.get('/policy-delivery-books/templates'))
        if (cancelled) return
        const rows = Array.isArray(payload) ? payload : []
        setTemplates(rows)
        setSelectedId(rows[0]?.id || null)
      } catch {
        if (!cancelled) show('증권전달북 템플릿을 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [show])

  useEffect(() => {
    const sections = Array.isArray(selected?.sections) ? selected.sections : []
    setSectionKeys(sections.filter((section) => section.defaultIncluded !== false).map((section) => section.key).filter(Boolean))
  }, [selected])

  useEffect(() => () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const clearPreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
    setPreviewUrl('')
  }

  const renderParams = () => ({
    params: {
      ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
      ...(deliveryDate ? { deliveryDate } : {}),
      ...(policyNumber.trim() ? { policyNumber: policyNumber.trim() } : {}),
      ...(selectedSections.length && sectionKeys.length ? { sectionKeys: sectionKeys.join(',') } : {}),
    },
  })

  const preview = async () => {
    if (!canRender) return
    setDownloading(true)
    try {
      const res = await api.get(`/policy-delivery-books/templates/${selected.id}/preview`, {
        ...renderParams(),
        responseType: 'blob',
      })
      clearPreview()
      setPreviewUrl(window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })))
    } catch {
      show('미리보기 PDF 생성에 실패했습니다.', 'error')
    } finally {
      setDownloading(false)
    }
  }

  const download = async () => {
    if (!canRender) return
    setDownloading(true)
    try {
      const res = await api.get(`/policy-delivery-books/templates/${selected.id}/download`, {
        ...renderParams(),
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res?.headers?.['content-type'] || 'application/pdf' })
      const filename = filenameFromDisposition(res?.headers?.['content-disposition'], `${selected.title || '증권전달북'}.pdf`)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('증권전달북 PDF를 다운로드했습니다.', 'success')
    } catch {
      show('증권전달북 PDF 다운로드에 실패했습니다.', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-slate-900">증권전달북</h2>
          {loading && <span className="text-xs text-slate-500">불러오는 중</span>}
        </div>


        {!hasReportPhoto && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="font-semibold">리포트용 사진을 아직 생성하지 않았습니다.</div>
            <div className="mt-1 leading-6">
              사진 없이도 증권전달북 미리보기와 다운로드는 가능하지만, 리포트용 사진이 있으면 설계사 소개와 전달북 구성이 더 풍부하게 표현됩니다.
            </div>
            <div className="mt-3">
              <Link to="/profile" className="inline-flex rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100">
                마이페이지에서 리포트용 사진 생성
              </Link>
            </div>
          </div>
        )}

        {templates.length > 0 ? (
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">증권 전달북 템플릿</span>
            <select
              className="mt-1 w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
              value={selectedId || ''}
              onChange={(e) => {
                setSelectedId(e.target.value)
                clearPreview()
              }}
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </select>
            {selected?.description && <div className="mt-1 text-sm text-slate-500">{selected.description}</div>}
          </label>
        ) : (
          <div className="rounded border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
            다운로드 가능한 템플릿이 없습니다.
          </div>
        )}

        {selected && (
          <div className="mt-4 grid gap-4">
            <div className="grid gap-3 md:grid-cols-3">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">고객명</span>
                <input
                  type="text"
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                  value={customerName}
                  maxLength={80}
                  onChange={(e) => {
                    setCustomerName(e.target.value)
                    clearPreview()
                  }}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">전달일</span>
                <input
                  type="date"
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                  value={deliveryDate}
                  onChange={(e) => {
                    setDeliveryDate(e.target.value)
                    clearPreview()
                  }}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">증권번호</span>
                <input
                  type="text"
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                  value={policyNumber}
                  maxLength={80}
                  onChange={(e) => {
                    setPolicyNumber(e.target.value)
                    clearPreview()
                  }}
                />
              </label>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={preview}
            disabled={!canRender || downloading}
            className="rounded border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            미리보기
          </button>
          <button
            type="button"
            onClick={download}
            disabled={!canRender || downloading}
            className="rounded bg-sgez-primary px-4 py-2 text-sm font-semibold text-white hover:bg-sgez-primary/90 disabled:opacity-50"
          >
            다운로드
          </button>
        </div>
      </div>

      {previewUrl && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <iframe title="증권전달북 미리보기" src={previewUrl} className="h-[640px] w-full rounded border border-slate-200" />
        </div>
      )}
    </div>
  )
}
